import { useCallback, useEffect, useMemo, useState } from "react";
import {
    DoorOpen,
    DoorClosed,
    Clock,
    CalendarRange,
    ChevronLeft,
    ChevronRight,
    Store,
    Pencil,
    CheckCircle2,
    AlertTriangle,
    Settings2,
    Info,
    MinusCircle,
} from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useI18n } from "@/context/I18nContext";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { PageHeader } from "@/components/layout/PageHeader";
import { EmptyState } from "@/components/common/EmptyState";
import CheckinScheduleDialog from "@/components/checkin/CheckinScheduleDialog";
import { OPS } from "@/constants/testIds";

const C = OPS.checkin;

function todayIso() {
    // Riyadh (UTC+3) business date — matches the backend's business day.
    const d = new Date(Date.now() + 3 * 3600 * 1000);
    return d.toISOString().slice(0, 10);
}

function shiftIso(iso, days) {
    const d = new Date(iso + "T00:00:00Z");
    d.setUTCDate(d.getUTCDate() + days);
    return d.toISOString().slice(0, 10);
}

function buildMonths(count = 12) {
    const out = [];
    const now = new Date(Date.now() + 3 * 3600 * 1000);
    let y = now.getUTCFullYear();
    let m = now.getUTCMonth() + 1;
    for (let i = 0; i < count; i++) {
        out.push(`${y}-${String(m).padStart(2, "0")}`);
        m -= 1;
        if (m < 1) { m = 12; y -= 1; }
    }
    return out;
}

function monthLabel(ym, lang) {
    if (!ym) return "";
    const [yy, mm] = ym.split("-").map(Number);
    return new Date(Date.UTC(yy, mm - 1, 1)).toLocaleDateString(
        lang === "ar" ? "ar-SA" : "en-US",
        { month: "long", year: "numeric" }
    );
}

function dateLabel(iso, lang) {
    if (!iso) return "";
    return new Date(iso + "T00:00:00Z").toLocaleDateString(
        lang === "ar" ? "ar-SA" : "en-US",
        { weekday: "long", day: "numeric", month: "short", year: "numeric" }
    );
}

// ---- one open/close status card -----------------------------------------
function StatusCard({ kind, field, status, scheduleDay, canRecord, isToday, onLive, onManual, t, lang, testId, nowBtnTestId }) {
    const [editing, setEditing] = useState(false);
    const [val, setVal] = useState("");
    const Icon = kind === "open" ? DoorOpen : DoorClosed;
    const scheduled = kind === "open" ? scheduleDay?.open : scheduleDay?.close;
    const dayClosed = scheduleDay?.closed;

    useEffect(() => { setEditing(false); }, [field, kind, status]);

    const beginEdit = () => {
        setVal(field?.time || "");
        setEditing(true);
    };
    const saveManual = async () => {
        if (!val || !/^([01]\d|2[0-3]):[0-5]\d$/.test(val)) {
            toast.error(t("checkin_time_invalid"));
            return;
        }
        await onManual(kind, val);
        setEditing(false);
    };

    return (
        <Card data-testid={testId} className="rounded-2xl border-[color:var(--border-soft)] shadow-[var(--shadow-sm)]">
            <CardContent className="p-5 space-y-3">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2.5">
                        <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-[color:var(--brand-orange-soft)]">
                            <Icon className="h-5 w-5 text-[color:var(--brand-orange)]" />
                        </span>
                        <div>
                            <p className="text-sm font-bold text-[color:var(--ink)]">
                                {kind === "open" ? t("checkin_open") : t("checkin_close")}
                            </p>
                            <p className="text-xs text-[color:var(--ink-muted)]">
                                {dayClosed
                                    ? t("checkin_closed_today")
                                    : <>{t("checkin_scheduled")}: <bdi dir="ltr" className="font-semibold text-[color:var(--ink)]">{scheduled || "—"}</bdi></>}
                            </p>
                        </div>
                    </div>
                    {field && !dayClosed ? (
                        field.compliance === "late" ? (
                            <Badge variant="destructive" className="gap-1">
                                <AlertTriangle className="h-3.5 w-3.5" />
                                {t("checkin_late")}{typeof field.minutes_late === "number" ? ` · ${field.minutes_late} ${t("checkin_min")}` : ""}
                            </Badge>
                        ) : field.compliance === "early_close" ? (
                            <Badge data-testid={C.earlyBadge(kind)} variant="warning" className="gap-1">
                                <Clock className="h-3.5 w-3.5" />
                                {t("checkin_early_close")}{typeof field.minutes_late === "number" ? ` · ${Math.abs(field.minutes_late)} ${t("checkin_min")}` : ""}
                            </Badge>
                        ) : field.compliance === "on_time" ? (
                            <Badge variant="success" className="gap-1">
                                <CheckCircle2 className="h-3.5 w-3.5" />
                                {t("checkin_on_time")}
                            </Badge>
                        ) : (
                            <Badge variant="secondary" className="gap-1">
                                <MinusCircle className="h-3.5 w-3.5" />
                                {t("checkin_not_applicable")}
                            </Badge>
                        )
                    ) : null}
                </div>

                {/* body */}
                {editing ? (
                    <div className="flex items-center gap-2">
                        <Input
                            data-testid={C.manualInput(kind)}
                            type="time"
                            value={val}
                            onChange={(e) => setVal(e.target.value)}
                            className="w-36"
                        />
                        <Button data-testid={C.manualSave(kind)} size="sm" onClick={saveManual}>{t("save")}</Button>
                        <Button data-testid={C.manualCancel(kind)} size="sm" variant="ghost" onClick={() => setEditing(false)}>{t("cancel")}</Button>
                    </div>
                ) : field ? (
                    <div className="flex items-end justify-between gap-3">
                        <div>
                            <p className="tnum text-3xl font-extrabold text-[color:var(--ink)]"><bdi dir="ltr">{field.time}</bdi></p>
                            <div className="mt-1 flex flex-wrap items-center gap-1.5 text-[11px]">
                                {field.manually_adjusted ? (
                                    <Badge variant="warning" className="text-[10px]">{t("checkin_manually_adjusted")}</Badge>
                                ) : (
                                    <Badge variant="secondary" className="text-[10px]">{t("checkin_live_stamp")}</Badge>
                                )}
                                <span className="text-[color:var(--ink-faint)]">
                                    {field.manually_adjusted
                                        ? `${t("checkin_adjusted_by")} ${field.adjusted_by_name || "—"}`
                                        : `${t("checkin_recorded_by")} ${field.recorded_by_name || "—"}`}
                                </span>
                            </div>
                        </div>
                        {canRecord ? (
                            <Button data-testid={C.editBtn(kind)} size="sm" variant="soft" className="gap-1.5" onClick={beginEdit}>
                                <Pencil className="h-3.5 w-3.5" />
                                {t("checkin_edit")}
                            </Button>
                        ) : null}
                    </div>
                ) : (
                    <div className="flex flex-col gap-3">
                        <p data-testid={`${testId}-empty`} className="text-sm italic text-[color:var(--ink-faint)]">
                            {t("checkin_not_recorded")}
                        </p>
                        {canRecord && !dayClosed ? (
                            <div className="flex flex-wrap items-center gap-2">
                                {isToday ? (
                                    <Button data-testid={nowBtnTestId} className="gap-2" onClick={() => onLive(kind)}>
                                        <Clock className="h-4 w-4" />
                                        {kind === "open" ? t("checkin_open_now") : t("checkin_close_now")}
                                    </Button>
                                ) : null}
                                <Button data-testid={C.editBtn(kind)} variant="soft" className="gap-1.5" onClick={beginEdit}>
                                    <Pencil className="h-3.5 w-3.5" />
                                    {t("checkin_enter_time")}
                                </Button>
                            </div>
                        ) : null}
                    </div>
                )}
            </CardContent>
        </Card>
    );
}

// ---- monthly compliance summary ------------------------------------------
function ComplianceStat({ label, pct, t, note }) {
    const val = pct === null || pct === undefined ? null : pct;
    const color = val === null ? "text-[color:var(--ink-faint)]"
        : val >= 90 ? "text-[color:var(--success)]"
        : val >= 70 ? "text-[color:var(--brand-brown)]"
        : "text-[color:var(--danger)]";
    return (
        <div className="rounded-xl surface-2 border border-[color:var(--border-soft)] p-4">
            <p className="text-xs text-[color:var(--ink-muted)] mb-1">{label}</p>
            <p className={`tnum text-3xl font-extrabold ${color}`}>{val === null ? "—" : `${val}%`}</p>
            {note}
        </div>
    );
}

export default function BranchCheckinPage() {
    const { t, lang, tn } = useI18n();
    const { isBranchManager } = useAuth();
    const [scope, setScope] = useState(null);
    const [branchId, setBranchId] = useState("");
    const [date, setDate] = useState(todayIso());
    const [data, setData] = useState(null);
    const [summary, setSummary] = useState(null);
    const [month, setMonth] = useState(todayIso().slice(0, 7));
    const [loading, setLoading] = useState(true);
    const [scheduleOpen, setScheduleOpen] = useState(false);

    const months = useMemo(() => buildMonths(12), []);
    const branch = useMemo(
        () => (scope?.branches || []).find((b) => b.id === branchId),
        [scope, branchId]
    );

    useEffect(() => {
        (async () => {
            try {
                const r = await api.get("/branches/checkin/scope");
                setScope(r.data);
                const first = r.data.branches?.[0];
                if (first) setBranchId(first.id);
            } catch (e) {
                toast.error(e?.response?.data?.detail || t("error_generic"));
            } finally {
                setLoading(false);
            }
        })();
    }, [t]);

    const loadDay = useCallback(async () => {
        if (!branchId) return;
        try {
            const r = await api.get(`/branches/${branchId}/checkin`, { params: { date } });
            setData(r.data);
        } catch (e) {
            setData(null);
        }
    }, [branchId, date]);

    const loadSummary = useCallback(async () => {
        if (!branchId) return;
        try {
            const r = await api.get(`/branches/${branchId}/checkin-summary`, { params: { month } });
            setSummary(r.data);
        } catch (e) {
            setSummary(null);
        }
    }, [branchId, month]);

    useEffect(() => { loadDay(); }, [loadDay]);
    useEffect(() => { loadSummary(); }, [loadSummary]);

    const recordLive = async (action) => {
        try {
            const r = await api.post(`/branches/${branchId}/checkin`, { action, date });
            setData(r.data);
            loadSummary();
            toast.success(t("checkin_saved"));
        } catch (e) {
            if (e?.response?.status === 409) {
                toast.error(t("checkin_already_recorded"));
                loadDay();
            } else {
                toast.error(e?.response?.data?.detail || t("error_generic"));
            }
        }
    };

    const recordManual = async (action, time) => {
        try {
            const r = await api.post(`/branches/${branchId}/checkin`, { action, time, date });
            setData(r.data);
            loadSummary();
            toast.success(t("checkin_saved"));
        } catch (e) {
            toast.error(e?.response?.data?.detail || t("error_generic"));
        }
    };

    if (loading) {
        return <div className="p-4"><Skeleton className="h-72 w-full" /></div>;
    }

    if (!scope?.branches?.length) {
        return (
            <div data-testid={C.page}>
                <EmptyState
                    testId={C.empty}
                    icon={Store}
                    title={t("checkin_no_branch")}
                    hint={t("checkin_no_branch_hint")}
                />
            </div>
        );
    }

    const sd = data?.schedule_day;
    const s = summary?.summary;

    return (
        <div data-testid={C.page} className="space-y-5">
            <PageHeader
                icon={DoorOpen}
                title={t("checkin_title")}
                subtitle={t("checkin_subtitle")}
                primaryAction={
                    scope.can_edit_schedule ? (
                        <Button data-testid={C.scheduleBtn} variant="soft" className="gap-2" onClick={() => setScheduleOpen(true)}>
                            <Settings2 className="h-4 w-4" />
                            {t("checkin_schedule_settings")}
                        </Button>
                    ) : null
                }
                actions={
                    <>
                        {isBranchManager && scope.branches.length === 1 ? (
                            <div className="inline-flex items-center gap-2 rounded-full surface-2 border border-[color:var(--border-soft)] px-3 h-9 text-sm font-medium text-[color:var(--ink)]">
                                <Store className="h-4 w-4 text-[color:var(--brand-brown)]" />
                                {tn(branch)}
                            </div>
                        ) : (
                            <Select value={branchId} onValueChange={setBranchId}>
                                <SelectTrigger data-testid={C.branchSelect} className="w-52">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {scope.branches.map((b) => (
                                        <SelectItem key={b.id} value={b.id}>{tn(b)}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        )}
                        <div className="inline-flex items-center gap-1">
                            <Button size="icon" variant="soft" data-testid={C.datePrev} onClick={() => setDate((d) => shiftIso(d, -1))} className="h-9 w-9">
                                <ChevronRight className="h-4 w-4 rtl:block ltr:hidden" />
                                <ChevronLeft className="h-4 w-4 ltr:block rtl:hidden" />
                            </Button>
                            <Input
                                data-testid={C.dateInput}
                                type="date"
                                value={date}
                                max={todayIso()}
                                onChange={(e) => setDate(e.target.value)}
                                className="w-40"
                            />
                            <Button size="icon" variant="soft" data-testid={C.dateNext} disabled={date >= todayIso()} onClick={() => setDate((d) => shiftIso(d, 1))} className="h-9 w-9">
                                <ChevronLeft className="h-4 w-4 rtl:block ltr:hidden" />
                                <ChevronRight className="h-4 w-4 ltr:block rtl:hidden" />
                            </Button>
                        </div>
                        <Button size="sm" variant={date === todayIso() ? "default" : "outline"} data-testid={C.todayBtn} onClick={() => setDate(todayIso())} className="h-9">
                            {t("checkin_today")}
                        </Button>
                    </>
                }
            />

            {/* day + schedule strip */}
            <div className="flex flex-wrap items-center gap-2 text-xs text-[color:var(--ink-muted)]">
                <span className="inline-flex items-center gap-1.5 rounded-lg surface-2 border border-[color:var(--border-soft)] px-2.5 py-1">
                    <CalendarRange className="h-3.5 w-3.5 text-[color:var(--brand-sky)]" />
                    <bdi dir="ltr" className="font-semibold text-[color:var(--ink)]">{dateLabel(date, lang)}</bdi>
                </span>
                {sd ? (
                    <span className="inline-flex items-center gap-1.5 rounded-lg surface-2 border border-[color:var(--border-soft)] px-2.5 py-1">
                        <Clock className="h-3.5 w-3.5 text-[color:var(--brand-sky)]" />
                        {sd.closed ? (
                            <span className="font-semibold text-[color:var(--danger)]">{t("checkin_closed_today")}</span>
                        ) : (
                            <>
                                {t("checkin_schedule_today")}:{" "}
                                <bdi dir="ltr" className="font-semibold text-[color:var(--ink)]">{sd.open} – {sd.close}</bdi>
                                {sd.is_default ? <span className="text-[color:var(--ink-faint)]"> ({t("checkin_default_hours")})</span> : null}
                            </>
                        )}
                    </span>
                ) : null}
                <span className="inline-flex items-center gap-1.5 text-[color:var(--ink-faint)]">
                    <Info className="h-3.5 w-3.5" /> {t("checkin_grace_note")} {scope.grace_minutes} {t("checkin_min")}
                    {scope.early_close_minutes ? <> · {t("checkin_early_close_note")} {scope.early_close_minutes} {t("checkin_min")}</> : null}
                </span>
            </div>

            {/* open / close cards */}
            <div className="grid gap-4 sm:grid-cols-2">
                <StatusCard
                    kind="open"
                    field={data?.open}
                    status={data?.open_status}
                    scheduleDay={sd}
                    canRecord={!!data?.can_record}
                    isToday={!!data?.is_today}
                    onLive={recordLive}
                    onManual={recordManual}
                    t={t}
                    lang={lang}
                    testId={C.openCard}
                    nowBtnTestId={C.openNowBtn}
                />
                <StatusCard
                    kind="close"
                    field={data?.close}
                    status={data?.close_status}
                    scheduleDay={sd}
                    canRecord={!!data?.can_record}
                    isToday={!!data?.is_today}
                    onLive={recordLive}
                    onManual={recordManual}
                    t={t}
                    lang={lang}
                    testId={C.closeCard}
                    nowBtnTestId={C.closeNowBtn}
                />
            </div>

            {/* monthly compliance summary */}
            <Card className="rounded-2xl border-[color:var(--border-soft)] shadow-[var(--shadow-sm)]">
                <CardContent className="p-5 space-y-4">
                    <div className="flex flex-wrap items-center justify-between gap-3">
                        <div>
                            <p className="text-sm font-bold text-[color:var(--ink)]">{t("checkin_summary_title")}</p>
                            <p className="text-xs text-[color:var(--ink-muted)]">{t("checkin_summary_desc")}</p>
                        </div>
                        <Select value={month} onValueChange={setMonth}>
                            <SelectTrigger data-testid={C.summaryMonth} className="w-44">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {months.map((m) => (
                                    <SelectItem key={m} value={m}>{monthLabel(m, lang)}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    {s ? (
                        <>
                            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                                <div data-testid={C.summaryOpenPct}>
                                    <ComplianceStat label={t("checkin_open_compliance")} pct={s.open_compliance_pct} t={t} />
                                </div>
                                <div data-testid={C.summaryClosePct}>
                                    <ComplianceStat
                                        label={t("checkin_close_compliance")}
                                        pct={s.close_compliance_pct}
                                        t={t}
                                        note={s.close_early ? (
                                            <p data-testid={C.summaryCloseEarly} className="text-[11px] font-semibold text-[color:var(--warning)]">
                                                {s.close_early} {t("checkin_early_close")}
                                            </p>
                                        ) : null}
                                    />
                                </div>
                                <div className="rounded-xl surface-2 border border-[color:var(--border-soft)] p-4">
                                    <p className="text-xs text-[color:var(--ink-muted)] mb-1">{t("checkin_scheduled_days")}</p>
                                    <p className="tnum text-3xl font-extrabold text-[color:var(--ink)]">{s.scheduled_days}</p>
                                    {s.closed_days ? <p className="text-[11px] text-[color:var(--ink-faint)]">{s.closed_days} {t("checkin_closed_days")}</p> : null}
                                </div>
                                <div className="rounded-xl surface-2 border border-[color:var(--border-soft)] p-4">
                                    <p className="text-xs text-[color:var(--ink-muted)] mb-1">{t("checkin_not_recorded_count")}</p>
                                    <p className="tnum text-3xl font-extrabold text-[color:var(--brand-brown)]">{s.open_not_recorded}</p>
                                    <p className="text-[11px] text-[color:var(--ink-faint)]">{t("checkin_open_word")}</p>
                                </div>
                            </div>
                            <p className="flex items-start gap-1.5 text-[11px] text-[color:var(--ink-faint)]">
                                <Info className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                                {t("checkin_summary_note")} {t("checkin_summary_early_note")}
                            </p>
                        </>
                    ) : (
                        <Skeleton className="h-24 w-full" />
                    )}
                </CardContent>
            </Card>

            {scope.can_edit_schedule ? (
                <CheckinScheduleDialog
                    branchId={branchId}
                    open={scheduleOpen}
                    onOpenChange={setScheduleOpen}
                    onSaved={() => { loadDay(); loadSummary(); }}
                />
            ) : null}
        </div>
    );
}
